import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, Typography,
} from '@mui/material';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { AuthService } from '../../services/auth.service';
import { MyBreakdownService } from '../../services/maintenance.service';
import { useToast } from '../../context/ToastContext';
import { MyBreakdownRequestView, mntLabel } from '../../interfaces/maintenance';
import ReportBreakdownDialog from './ReportBreakdownDialog';
import FocusedRowChip from '../common/FocusedRowChip';
import { useFocusedRow } from '../../hooks/useFocusedRow';
import { REQUEST_STATUS_COLOR, WO_STATUS_COLOR, fmtDateTime } from './woCommon';

/** Self-service page: the breakdowns I reported, and a button to report a new one. */
const MyBreakdownSection: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [rows, setRows] = useState<MyBreakdownRequestView[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [loading, setLoading] = useState(true);
    const [params, setParams] = useSearchParams();
    const scannedEquipmentId = params.get('equipmentId') ?? undefined;
    const [open, setOpen] = useState(!!scannedEquipmentId);
    const [presetId] = useState(scannedEquipmentId);
    const { focusId, clear: clearFocus } = useFocusedRow();
    useEffect(() => {
        // Opened from a QR scan: drop the param so a refresh does not pop the dialog again.
        if (scannedEquipmentId) setParams(p => { const n = new URLSearchParams(p); n.delete('equipmentId'); return n; }, { replace: true });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const load = useCallback(async () => {
        try {
            setLoading(true);
            if (focusId) {
                setRows([await MyBreakdownService.getMine(focusId)]);
                setTotal(1);
                return;
            }
            const res = await MyBreakdownService.listMine(institutionId, page, size);
            setRows(res.content);
            setTotal(res.totalElements);
        } catch (e) {
            toast.showApiError(e, 'Failed to load your breakdown reports');
        } finally {
            setLoading(false);
        }
    }, [institutionId, page, size, focusId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const cancel = async (id: string) => {
        try {
            await MyBreakdownService.cancelMine(id);
            toast.showSuccess('Report cancelled');
            load();
        } catch (e) {
            toast.showApiError(e, 'Failed to cancel the report');
        }
    };

    return (
        <Box sx={{ p: 2 }} data-help-page="maint.myRequests.page">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h5">My breakdown reports</Typography>
                <Button data-help="maint.myRequests.report" variant="contained" startIcon={<ReportProblemIcon />} onClick={() => setOpen(true)}>
                    Report a breakdown
                </Button>
            </Box>
            <FocusedRowChip focusId={focusId} noun="report" onClear={clearFocus} />
            <Paper data-help-section="maint.myRequests.table">
                {loading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="maint.myRequests.col.no">No.</TableCell>
                                    <TableCell data-help="maint.myRequests.col.machine">Machine / place</TableCell>
                                    <TableCell data-help="maint.myRequests.col.problem">Problem</TableCell>
                                    <TableCell data-help="maint.myRequests.col.when">Reported</TableCell>
                                    <TableCell data-help="maint.myRequests.col.status">Status</TableCell>
                                    <TableCell data-help="maint.myRequests.col.wo">Work order</TableCell>
                                    <TableCell data-help="maint.myRequests.col.actions" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 && (
                                    <TableRow><TableCell colSpan={7} align="center">You have not reported any breakdowns</TableCell></TableRow>
                                )}
                                {rows.map(r => (
                                    <TableRow key={r.id}>
                                        <TableCell>{r.requestNo}</TableCell>
                                        <TableCell>{r.equipmentCode ? `${r.equipmentCode} ${r.equipmentName ?? ''}` : r.locationName ?? '-'}</TableCell>
                                        <TableCell sx={{ maxWidth: 280 }}>{r.description}</TableCell>
                                        <TableCell>{fmtDateTime(r.createdAt)}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={mntLabel(r.status)} color={REQUEST_STATUS_COLOR[r.status]} />
                                            {r.status === 'REJECTED' && r.rejectionReason && (
                                                <Typography variant="caption" display="block">{r.rejectionReason}</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {r.workOrderNo ? (
                                                <>
                                                    {r.workOrderNo}{' '}
                                                    {r.workOrderStatus && (
                                                        <Chip size="small" label={mntLabel(r.workOrderStatus)} color={WO_STATUS_COLOR[r.workOrderStatus]} />
                                                    )}
                                                    {r.technicianName && <Typography variant="caption" display="block">{r.technicianName}</Typography>}
                                                </>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell align="right">
                                            {r.status === 'SUBMITTED' && (
                                                <Button data-help="maint.myRequests.cancel" size="small" color="inherit" onClick={() => cancel(r.id)}>Cancel</Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <TablePagination
                    component="div" count={total} page={page} rowsPerPage={size}
                    rowsPerPageOptions={[10, 20, 50]}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={e => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Paper>
            <ReportBreakdownDialog open={open} initialEquipmentId={presetId} onClose={() => setOpen(false)} onCreated={() => { setPage(0); load(); }} />
        </Box>
    );
};

export default MyBreakdownSection;
